import { Component, type ReactNode } from 'react';

interface LazyAppBoundaryProps {
  /** The player-facing name of the app the chunk holds ("Slime Wars"). */
  readonly name: string;
  readonly children: ReactNode;
  /** Retry action; default: reload the page (a failed `lazy()` import stays failed). */
  readonly onRetry?: () => void;
}

interface LazyAppBoundaryState {
  readonly failed: boolean;
}

/**
 * A lazily loaded app chunk that fails to load (offline, or a page from before a deploy asking
 * for a chunk hash that no longer exists) shows a calm message with a retry instead of a page
 * stuck on its loading line (W015 smoke follow-up).
 */
export class LazyAppBoundary extends Component<LazyAppBoundaryProps, LazyAppBoundaryState> {
  override state: LazyAppBoundaryState = { failed: false };

  static getDerivedStateFromError(): LazyAppBoundaryState {
    return { failed: true };
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    const retry = this.props.onRetry ?? (() => window.location.reload());
    return (
      <div className="app-loading" role="alert">
        <p>{`${this.props.name} could not be loaded. Check your connection and try again.`}</p>
        <button type="button" className="app-loading__retry" onClick={retry}>
          Try again
        </button>
      </div>
    );
  }
}
